import { DateInput } from "@msuite/ui/date-input";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { crewDefinitionTicketAssignmentSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { CrewDefinitionPicker } from "@/components/pickers/crew-definition-picker";
import { client } from "@/hono";

/**
 * The body the route takes, plus the crew it is addressed to. The crew is a path parameter rather
 * than a field, so it has no place in the shared schema and is composed on here instead — an
 * intersection because the shared half carries a transform and cannot be extended.
 */
const scheduleCrewFormSchema = z
	.object({ crewDefinitionId: z.string().min(1) })
	.and(crewDefinitionTicketAssignmentSchema);

type ScheduleCrewFormSchema = z.infer<typeof scheduleCrewFormSchema>;

/** Props Interface */
interface ScheduleCrewModalProps extends ModalProps {
	ticketId: string;
	/** The ticket's window, widened to whole weeks where its dates name weeks. */
	windowStart: string;
	windowEnd: string;
	/** Where the work sits inside that window — the stretch a crew is offered by default. */
	workStart: string;
	workEnd: string;
}

/**
 * Put a standing crew on a ticket for a stretch of days.
 *
 * The stretch opens on the ticket's **work**, not its whole window: a fortnight holding three days
 * of work wants a crew for the three, and widening it is the deliberate act. The ticket's window is
 * the outer limit at both ends, because the server refuses a stretch reaching past it.
 */
export function ScheduleCrewModal({
	isOpen,
	onClose,
	ticketId,
	windowStart,
	windowEnd,
	workStart,
	workEnd,
}: ScheduleCrewModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const methods = useForm<ScheduleCrewFormSchema>({
		resolver: resolver(scheduleCrewFormSchema),
		defaultValues: {
			crewDefinitionId: "",
			ticketId,
			startDate: workStart,
			endDate: workEnd,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ScheduleCrewFormSchema) {
		const response = await client.crew_definitions[":crewDefinitionId"]["ticket-assignments"].$post(
			{
				param: { crewDefinitionId: json.crewDefinitionId },
				json: { ticketId, startDate: json.startDate, endDate: json.endDate },
			},
		);
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["crew-definitions", "tickets"]) });
		onClose();
	}

	/** Computed */
	const watchedStartDate = methods.watch("startDate");
	const watchedEndDate = methods.watch("endDate");

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				title={t("planning.crews.schedule_crew")}
				width="500px"
				data-cy="schedule-crew-modal"
			>
				<Modal.Body>
					<CrewDefinitionPicker
						control={methods.control}
						name="crewDefinitionId"
						label={t("planning.crews.scheduled_crew")}
						isRequired
						startDate={watchedStartDate}
						endDate={watchedEndDate}
					/>
					<div className="gap-4 grid grid-cols-2">
						<DateInput
							control={methods.control}
							name="startDate"
							label={t("planning.crews.scheduled_from")}
							isRequired
							minDate={windowStart}
							maxDate={watchedEndDate || windowEnd}
						/>
						<DateInput
							control={methods.control}
							name="endDate"
							label={t("planning.crews.scheduled_until")}
							isRequired
							minDate={watchedStartDate || windowStart}
							maxDate={windowEnd}
						/>
					</div>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
